import { FC, ReactNode, useState } from 'react'
import { TrendsChartDescriptionFormControl, CategoryMenuProps } from './trendsChartDescription.styles'
import { TrendsChartSelectDropdownIcon } from 'ui/components/svg'
import { ListItemText, MenuItem, OutlinedInput, SelectProps, Select } from '@mui/material'
import theme from 'theme'

interface Props extends SelectProps {
  options: string[]
  value: any
  handleChange: any
  minWidth?: string
  noborder?: string
  labeltext?: string
  disabled: boolean
  hideSelected?: boolean
  raise?: boolean
  modalRaise?: boolean
  inlineLabel?: string
}

const TrendsChartSelectField: FC<Props> = ({
  options,
  labeltext,
  handleChange,
  placeholder,
  noborder,
  minWidth,
  value,
  disabled,
  hideSelected,
  raise,
  modalRaise,
  inlineLabel,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const handleClick = (event: any) => {
    if (disabled) return
    setAnchorEl(event.currentTarget)
  }

  // Opt-in: prefix the rendered value with a muted label shown inside the box (e.g. "Frequency:").
  const withLabel = (node: ReactNode) =>
    inlineLabel ? (
      <>
        <span className="inline-filter-label">{inlineLabel}</span> {node}
      </>
    ) : (
      node
    )

  return (
    <TrendsChartDescriptionFormControl
      sx={{ minWidth }}
      autowidth={inlineLabel ? 'true' : undefined}
      isselected={
        hideSelected
          ? 'false'
          : placeholder !== 'Sort' && (value.length > 1 || (value.length === 1 && value[0] !== ''))
          ? 'true'
          : 'false'
      }
      is_opened={anchorEl ? 'true' : 'false'}
      is_disabled={disabled ? 'true' : 'false'}
      noborder={noborder}
    >
      <Select
        labelId={`library-select-${labeltext}-label`}
        id={`library-select-${labeltext}`}
        displayEmpty
        MenuProps={{
          ...CategoryMenuProps,
          sx: {
            zIndex: raise ? 3001 : modalRaise ? 100005 : '',
            display: anchorEl ? 'block' : 'none'
          },
          PaperProps: {
            ...CategoryMenuProps.PaperProps,
            style: { maxHeight: 'unset', display: anchorEl ? 'block' : 'none', zIndex: raise ? 3001 : '' }
          },
          anchorEl,
          open: Boolean(anchorEl),
          onClose: () => setAnchorEl(null)
        }}
        sx={{
          minWidth,
          '& .MuiSelect-icon': { transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)' }
        }}
        IconComponent={(props) => <TrendsChartSelectDropdownIcon {...props} />}
        renderValue={(selected) => {
          if (Array.isArray(selected)) {
            const pruned = selected.filter((s: string) => s !== '')
            if (pruned.length === 0) {
              return withLabel(placeholder)
            }
            if (pruned.length === 1) {
              return withLabel(`${pruned[0]} +${pruned.length - 1}`)
            }
            return withLabel(pruned[0])
          }

          if (labeltext) {
            return withLabel(typeof noborder !== 'undefined' ? `${labeltext} ${selected}` : selected)
          }
          return withLabel(typeof noborder !== 'undefined' ? `Sort by: ${selected}` : selected)
        }}
        inputProps={{ 'aria-label': 'Without label' }}
        {...rest}
        value={value}
        onChange={handleChange}
        input={<OutlinedInput onMouseDown={handleClick} />}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            value={option}
            onClick={() => {
              if (!rest.multiple) {
                setAnchorEl(null)
              }
            }}
            disableRipple
          >
            <ListItemText primary={option} />
            {value === option && (
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2.625 7.4375L6.125 10.9375L11.375 3.0625"
                  stroke={theme.colors.textPrimary}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </MenuItem>
        ))}
      </Select>
    </TrendsChartDescriptionFormControl>
  )
}

export default TrendsChartSelectField
